import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  ArrowLeft, Plus, Minus, Upload, X, Check, ShoppingBag, 
  MessageCircle, Trash2, ZoomIn, ChevronDown, Filter, Grid3X3,
  FileText, Link2, Download, ExternalLink, Palette, User,
  CheckCircle2, Circle, ChevronRight, ChevronLeft, Package, Sparkles, FileEdit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';
import CartDrawer from '../components/CartDrawer';
import CustomerModal from '../components/CustomerModal';
import { getCevadorConfig, getCevadorModels, getCevadorCategoryCounts, uploadCevadorCustomImage, getSettings } from '../services/api';
import { initMetaPixel, trackViewContent, trackLead, trackContact, trackAddToCart, trackInitiateCheckout } from '../utils/metaPixel';

const MODELS_PER_PAGE = 24; // Modelos por página (carregamento progressivo)

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const ModelCard = React.memo(({ model, qty, onQuantityChange, onSetQuantity, onPreview }) => {
  const imageUrl = resolveImageUrl(model.image_url);
  return (
    <div 
      className={`border-2 rounded-xl overflow-hidden transition-all ${
        qty > 0 ? 'border-[#195C75] shadow-md' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div 
        className="aspect-square bg-slate-100 relative cursor-pointer group"
        onClick={() => imageUrl && onPreview(imageUrl, model.name)}
      >
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={model.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
            Sem imagem
          </div>
        )}
        {imageUrl && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}
        {qty > 0 && (
          <div className="absolute top-1 right-1 bg-[#195C75] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
            {qty}
          </div>
        )}
      </div>
      <div className="p-2">
        <h3 className="font-medium text-xs text-slate-800 truncate mb-2" title={model.name}>
          {model.name}
        </h3>
        <div className="flex items-center justify-between">
          <button
            onClick={() => onQuantityChange(model.id, -1)}
            disabled={qty === 0}
            className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-30"
            data-testid={`model-minus-${model.id}`}
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            min="0"
            value={qty || ''}
            onChange={(e) => onSetQuantity(model.id, parseInt(e.target.value) || 0)}
            placeholder="0"
            className="w-10 text-center font-semibold text-[#195C75] text-sm border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-[#195C75] rounded"
          />
          <button
            onClick={() => onQuantityChange(model.id, 1)}
            className="w-6 h-6 rounded-full bg-[#195C75] text-white flex items-center justify-center"
            data-testid={`model-plus-${model.id}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
});

const CevadorPage = () => {
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedModels, setSelectedModels] = useState({});
  const [totalModels, setTotalModels] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryCounts, setCategoryCounts] = useState([]); // Category counts from backend
  
  // Step wizard state
  const [currentStep, setCurrentStep] = useState(1);
  
  // Custom arts state
  const [customArts, setCustomArts] = useState([]);
  
  // Art request modal state
  const [showArtRequestModal, setShowArtRequestModal] = useState(false);
  const [artRequestDescription, setArtRequestDescription] = useState('');
  const [artRequestReference, setArtRequestReference] = useState(null);
  const [artRequestReferencePreview, setArtRequestReferencePreview] = useState(null);
  
  // Link input state
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInputValue, setLinkInputValue] = useState('');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Order observation
  const [orderObservation, setOrderObservation] = useState('');

  const { addItem, openCart, getTotalItems, items: cartItems } = useCart();
  const { customer, isLoggedIn } = useCustomer();
  const totalCartItems = getTotalItems();
  const fileInputRef = useRef(null);
  const artReferenceInputRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const categoryDropdownDesktopRef = useRef(null);
  const prevCartItemsLength = useRef(cartItems.length);
  const loadMoreRef = useRef(null);

  // Reset page when cart is cleared (after sending quote)
  useEffect(() => {
    // If cart went from having items to being empty, reset the page
    if (prevCartItemsLength.current > 0 && cartItems.length === 0) {
      setSelectedModels({});
      setCustomArts([]);
      setOrderObservation('');
      setCurrentStep(1);
    }
    prevCartItemsLength.current = cartItems.length;
  }, [cartItems.length]);

  useEffect(() => {
    fetchInitialData();
    
    // Initialize Meta Pixel
    initMetaPixel();
    
    // Track ViewContent when page loads
    trackViewContent('Página Cevador', 'Montagem de Pedido', 0);
  }, []);

  // Handle browser back button when image preview is open
  useEffect(() => {
    if (imagePreview) {
      // Add a history entry when image preview opens
      window.history.pushState({ imagePreview: true }, '');
      
      const handlePopState = (event) => {
        // When back button is pressed, close the image preview instead of navigating away
        if (imagePreview) {
          setImagePreview(null);
          // Prevent default back navigation by pushing state again if we still have preview
          event.preventDefault();
        }
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [imagePreview]);

  // Fetch initial data (config, settings, first page of models)
  const fetchInitialData = async () => {
    try {
      const [configData, modelsResponse, settingsData, categoryCountsData] = await Promise.all([
        getCevadorConfig(),
        getCevadorModels(1, MODELS_PER_PAGE),
        getSettings(),
        getCevadorCategoryCounts()
      ]);
      setConfig(configData);
      setModels(modelsResponse.models || []);
      setTotalModels(modelsResponse.total || 0);
      setHasMore(modelsResponse.has_more || false);
      setCurrentPage(1);
      setSettings(settingsData);
      setCategoryCounts(categoryCountsData.categories || []);
    } catch (error) {
      console.error('Error fetching cevador data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more models when scrolling
  const loadMoreModels = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await getCevadorModels(nextPage, MODELS_PER_PAGE, selectedCategory, searchQuery);
      setModels(prev => [...prev, ...(response.models || [])]);
      setHasMore(response.has_more || false);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more models:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentPage, selectedCategory, searchQuery]);

  // Refetch models when filter changes
  const refetchModels = async (category, search) => {
    setLoading(true);
    setModels([]);
    setCurrentPage(1);
    try {
      const response = await getCevadorModels(1, MODELS_PER_PAGE, category, search);
      setModels(response.models || []);
      setTotalModels(response.total || 0);
      setHasMore(response.has_more || false);
    } catch (error) {
      console.error('Error refetching models:', error);
    } finally {
      setLoading(false);
    }
  };

  // Categories with counts from backend
  const categories = useMemo(() => {
    return categoryCounts;
  }, [categoryCounts]);

  // Filter models locally (for search/category changes while data loads)
  const filteredModels = useMemo(() => {
    let filtered = models;
    
    // Local filtering only for client-side search within loaded models
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(m => 
        m.name.toLowerCase().includes(query) ||
        (m.categories && m.categories.some(c => c.toLowerCase().includes(query)))
      );
    }
    
    return filtered;
  }, [models, searchQuery]);

  // Models to display
  const displayedModels = filteredModels;

  // Reset and refetch when category changes
  useEffect(() => {
    // Skip initial render and when category is 'all' with no search
    if (selectedCategory === 'all' && !searchQuery) return;
    refetchModels(selectedCategory, searchQuery);
  }, [selectedCategory]);

  // Debounced search - only refetch when search query actually changes
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    // Skip if search didn't actually change
    if (prevSearchRef.current === searchQuery) return;
    prevSearchRef.current = searchQuery;
    
    const timer = setTimeout(() => {
      refetchModels(selectedCategory, searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory]);

  // Infinite scroll - load more when user scrolls to the bottom
  useEffect(() => {
    if (!loadMoreRef.current) return;
    
    const currentRef = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreModels();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    observer.observe(currentRef);
    
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMoreModels]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileRef = categoryDropdownRef.current;
      const desktopRef = categoryDropdownDesktopRef.current;
      
      if (mobileRef && !mobileRef.contains(event.target) && 
          desktopRef && !desktopRef.contains(event.target)) {
        setShowCategoryDropdown(false);
      } else if (mobileRef && !mobileRef.contains(event.target) && !desktopRef) {
        setShowCategoryDropdown(false);
      } else if (!mobileRef && desktopRef && !desktopRef.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate price based on quantity
  const getUnitPrice = (quantity) => {
    if (!config?.prices || config.prices.length === 0) return 0;
    
    const qty = parseInt(quantity) || 0;
    const priceRange = config.prices.find(p => {
      if (p.max_quantity === null) {
        return qty >= p.min_quantity;
      }
      return qty >= p.min_quantity && qty <= p.max_quantity;
    });
    
    return priceRange?.price || config.prices[config.prices.length - 1]?.price || 0;
  };

  // Calculate total selected quantity
  const getTotalSelectedQuantity = () => {
    const modelsQty = Object.values(selectedModels).reduce((sum, qty) => sum + qty, 0);
    const customQty = customArts.reduce((sum, art) => sum + art.quantity, 0);
    return modelsQty + customQty;
  };

  // Handle model quantity change
  const handleModelQuantityChange = useCallback((modelId, delta) => {
    setSelectedModels(prev => {
      const current = prev[modelId] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const { [modelId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [modelId]: newQty };
    });
  }, []);

  const handleSetModelQuantity = useCallback((modelId, value) => {
    setSelectedModels(prev => {
      if (value === 0) {
        const { [modelId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [modelId]: value };
    });
  }, []);

  const handlePreviewImage = useCallback((url, name) => {
    setImagePreview({ url, name });
  }, []);

  // Handle custom image upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const isImage = file.type.startsWith('image/');
      const result = await uploadCevadorCustomImage(file);
      
      setCustomArts(prev => [...prev, {
        id: Date.now().toString(),
        type: 'file',
        fileName: file.name,
        fileType: file.type,
        fileUrl: result.image_url,
        isImage: isImage,
        quantity: 1
      }]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erro ao enviar arquivo');
    }
    
    e.target.value = '';
  };

  // Handle link submission
  const handleLinkSubmit = () => {
    if (!linkInputValue.trim()) return;
    
    setCustomArts(prev => [...prev, {
      id: Date.now().toString(),
      type: 'link',
      link: linkInputValue.trim(),
      quantity: 1
    }]);
    
    setLinkInputValue('');
    setShowLinkInput(false);
  };

  // Handle art request submission
  const handleArtRequestSubmit = async () => {
    if (!artRequestDescription.trim()) {
      alert('Por favor, descreva o que você quer na arte');
      return;
    }
    
    let referenceUrl = null;
    
    if (artRequestReference) {
      try {
        const result = await uploadCevadorCustomImage(artRequestReference);
        referenceUrl = result.image_url;
      } catch (error) {
        console.error('Error uploading reference:', error);
      }
    }
    
    setCustomArts(prev => [...prev, {
      id: Date.now().toString(),
      type: 'request',
      description: artRequestDescription.trim(),
      referenceUrl: referenceUrl,
      referenceFileName: artRequestReference?.name,
      quantity: 1
    }]);
    
    setArtRequestDescription('');
    setArtRequestReference(null);
    setArtRequestReferencePreview(null);
    setShowArtRequestModal(false);
  };

  // Handle reference file selection for art request
  const handleReferenceFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setArtRequestReference(file);
    
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setArtRequestReferencePreview(previewUrl);
    } else {
      setArtRequestReferencePreview(null);
    }
    
    e.target.value = '';
  };

  // Handle custom art quantity change - direct set to avoid double events
  const handleCustomArtQuantityChange = (index, newQuantity) => {
    setCustomArts(prev => {
      const updated = [...prev];
      updated[index].quantity = Math.max(1, newQuantity);
      return updated;
    });
  };

  // Remove custom art
  const removeCustomArt = (index) => {
    setCustomArts(prev => prev.filter((_, i) => i !== index));
  };

  // Add to cart
  const handleAddToCart = () => {
    const totalSelected = getTotalSelectedQuantity();
    const minQty = config?.min_quantity || 10;
    
    if (totalSelected < minQty) {
      alert(`Quantidade mínima: ${minQty} unidades`);
      return;
    }

    const unitPrice = getUnitPrice(totalSelected);

    // Add selected models to cart
    Object.entries(selectedModels).forEach(([modelId, quantity]) => {
      if (quantity > 0) {
        const model = models.find(m => m.id === modelId);
        if (model) {
          const lightImageUrl = model.image_url && model.image_url.startsWith('data:')
            ? `${process.env.REACT_APP_BACKEND_URL}/api/cevador/models/${modelId}/image`
            : resolveImageUrl(model.image_url);
          addItem({
            id: `cevador-${modelId}`,
            name: `Cevador - ${model.name}`,
            image_url: lightImageUrl,
            prices: [{ min_quantity: 1, max_quantity: null, price: unitPrice }]
          }, quantity, orderObservation ? `Cevador personalizado | Obs: ${orderObservation}` : 'Cevador personalizado');
        }
      }
    });

    // Add custom arts to cart
    customArts.forEach((art, index) => {
      if (art.quantity > 0) {
        let name = '';
        let notes = orderObservation ? `Obs: ${orderObservation} | ` : '';
        let imageUrl = null;
        let fileUrl = null;
        let artLink = null;
        
        if (art.type === 'file') {
          name = `Cevador - Arte ${index + 1} (${art.fileName})`;
          notes += `Arquivo enviado: ${art.fileName}`;
          if (art.isImage) imageUrl = art.fileUrl;
          fileUrl = art.fileUrl;
        } else if (art.type === 'link') {
          name = `Cevador - Arte ${index + 1} (Link)`;
          notes += `Link da arte: ${art.link}`;
          artLink = art.link;
        } else if (art.type === 'request') {
          name = `Cevador - Arte ${index + 1} (Criar Arte)`;
          notes += `Solicitação de arte: ${art.description}`;
          if (art.referenceUrl) {
            notes += ` | Referência: ${art.referenceFileName || 'anexada'}`;
            fileUrl = art.referenceUrl;
          }
        }
        
        addItem({
          id: `cevador-custom-${art.id}`,
          name: name,
          image_url: imageUrl,
          file_url: fileUrl,
          art_link: artLink,
          art_type: art.type,
          art_description: art.type === 'request' ? art.description : null,
          prices: [{ min_quantity: 1, max_quantity: null, price: unitPrice }]
        }, art.quantity, notes);
      }
    });

    // Track AddToCart event for Meta Pixel
    trackAddToCart('Cevadores Personalizados', totalSelected * unitPrice, totalSelected);

    openCart();
  };

  // Open WhatsApp with order summary
  const handleWhatsApp = () => {
    const totalSelected = getTotalSelectedQuantity();
    const unitPrice = getUnitPrice(totalSelected);
    const total = totalSelected * unitPrice;

    let message = `*Pedido de Cevadores*\n\n`;
    message += `*Quantidade Total:* ${totalSelected} unidades\n`;
    message += `*Preço Unitário:* R$ ${unitPrice.toFixed(2).replace('.', ',')}\n`;
    message += `*Total Estimado:* R$ ${total.toFixed(2).replace('.', ',')}\n\n`;
    message += `*Itens:*\n`;

    Object.entries(selectedModels).forEach(([modelId, quantity]) => {
      if (quantity > 0) {
        const model = models.find(m => m.id === modelId);
        if (model) {
          message += `- ${model.name}: ${quantity} un\n`;
        }
      }
    });

    customArts.forEach((art, index) => {
      if (art.quantity > 0) {
        if (art.type === 'file') {
          message += `- Arte Personalizada (${art.fileName}): ${art.quantity} un\n`;
        } else if (art.type === 'link') {
          message += `- Arte via Link: ${art.quantity} un\n`;
        } else if (art.type === 'request') {
          message += `- Criar Arte (${art.description.substring(0, 30)}...): ${art.quantity} un\n`;
        }
      }
    });

    if (orderObservation) {
      message += `\n*Observação:* ${orderObservation}`;
    }

    const phone = settings?.whatsapp?.replace(/\D/g, '') || '';
    
    // Track Contact and Lead event for Meta Pixel
    trackContact('whatsapp_cevador');
    trackLead('WhatsApp - Pedido Cevador', 'whatsapp_order');
    
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header skeleton */}
        <div className="bg-[#195C75] text-white py-4 px-6">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <div className="w-8 h-8 bg-white/20 rounded animate-pulse" />
            <div className="h-6 w-48 bg-white/20 rounded animate-pulse" />
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Info card skeleton */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <div className="flex justify-between items-start">
              <div className="space-y-3 flex-1">
                <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse" />
              </div>
              <div className="flex gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="w-20 h-16 bg-slate-200 rounded animate-pulse" />
                ))}
              </div>
            </div>
          </div>
          
          {/* Steps skeleton */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <div className="flex justify-between">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-slate-200 rounded-full animate-pulse" />
                  <div className="h-4 w-24 bg-slate-200 rounded animate-pulse hidden sm:block" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Models grid skeleton */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="h-6 w-48 bg-slate-200 rounded animate-pulse mb-6" />
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="border-2 border-slate-100 rounded-xl overflow-hidden">
                  <div className="aspect-square bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 animate-pulse" />
                  <div className="p-2 space-y-2">
                    <div className="h-3 w-16 bg-slate-200 rounded animate-pulse" />
                    <div className="h-8 bg-slate-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!config?.active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Página não disponível</h1>
          <p className="text-slate-600">Esta página está temporariamente desativada.</p>
          <Button onClick={() => window.location.href = '/'} className="mt-4">
            Voltar ao Catálogo
          </Button>
        </div>
      </div>
    );
  }

  const totalSelected = getTotalSelectedQuantity();
  const unitPrice = getUnitPrice(totalSelected);
  const totalPrice = totalSelected * unitPrice;
  const minQty = config?.min_quantity || 10;
  const isValidOrder = totalSelected >= minQty;

  // Get selected models count
  const selectedModelsCount = Object.values(selectedModels).filter(q => q > 0).length;
  const customArtsCount = customArts.length;

  // Step navigation helpers
  const canGoToStep2 = totalSelected >= minQty;

  const steps = [
    { number: 1, title: 'Selecionar Modelos', icon: Package },
    { number: 2, title: 'Resumo', icon: FileEdit }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Compact */}
      <header className="bg-[#195C75] text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-3 py-2 lg:px-4 lg:py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:gap-3">
              <div>
                <h1 className="text-base lg:text-lg font-bold leading-tight">Cevadores</h1>
                <p className="text-[10px] lg:text-xs text-white/80 hidden sm:block">Monte seu pedido em 2 etapas</p>
              </div>
            </div>
            <div className="flex items-center gap-1 lg:gap-2">
              {isLoggedIn ? (
                <a
                  href="/cliente"
                  className="flex items-center gap-2 px-2 py-1 hover:bg-white/10 rounded-lg transition-colors"
                  title={`Olá, ${customer?.name?.split(' ')[0]}`}
                >
                  <div className="w-6 h-6 lg:w-7 lg:h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold">
                      {customer?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium max-w-[80px] truncate">
                    {customer?.name?.split(' ')[0]}
                  </span>
                </a>
              ) : (
                <button
                  onClick={() => setShowCustomerModal(true)}
                  className="flex items-center gap-1.5 px-2 py-1 hover:bg-white/10 rounded-lg transition-colors"
                  title="Entrar"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:block text-sm">Entrar</span>
                </button>
              )}
              
              <button
                onClick={openCart}
                className="relative p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ShoppingBag className="w-5 h-5" />
                {totalCartItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-[#195C75] rounded-full text-[10px] font-bold flex items-center justify-center">
                    {totalCartItems}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Info Section - Fixed below header */}
      <div className="sticky top-[44px] lg:top-[48px] z-40 bg-slate-50 pb-1 lg:pb-2">
        <div className="max-w-6xl mx-auto px-3 pt-2 lg:px-4 lg:pt-2">
          {/* Desktop: Compact single row with all info */}
          <div className="hidden lg:block bg-white rounded-lg p-3 shadow-sm mb-2">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Badges */}
              <div className="flex items-center gap-2">
                <div className="bg-[#195C75]/10 text-[#195C75] px-2.5 py-1 rounded-lg text-xs font-medium">
                  Mínimo: {minQty} un
                </div>
                <div className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs">
                  Ø 16cm
                </div>
              </div>
              
              {/* Center: Price Table inline */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Preços:</span>
                {config.prices.map((range, index) => (
                  <div 
                    key={index}
                    className={`px-2 py-1 rounded border text-center ${
                      totalSelected >= range.min_quantity && (range.max_quantity === null || totalSelected <= range.max_quantity)
                        ? 'border-[#195C75] bg-[#195C75]/5'
                        : 'border-slate-200'
                    }`}
                  >
                    <span className="text-[10px] text-slate-500 mr-1">
                      {range.max_quantity ? `${range.min_quantity}-${range.max_quantity}` : `${range.min_quantity}+`}
                    </span>
                    <span className="text-sm font-bold text-[#195C75]">
                      R${range.price.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Right: Steps indicator */}
              <div className="flex items-center gap-1">
                {steps.map((step, index) => {
                  const isActive = currentStep === step.number;
                  const isCompleted = currentStep > step.number;
                  const isClickable = step.number === 1 || 
                    (step.number === 2 && canGoToStep2);
                  
                  return (
                    <React.Fragment key={step.number}>
                      <button
                        onClick={() => isClickable && setCurrentStep(step.number)}
                        disabled={!isClickable}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-all ${
                          isActive 
                            ? 'bg-[#195C75] text-white' 
                            : isCompleted
                              ? 'bg-green-100 text-green-700'
                              : isClickable
                                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          isActive ? 'bg-white/20' : isCompleted ? 'bg-green-200' : 'bg-slate-200'
                        }`}>
                          {isCompleted ? '✓' : step.number}
                        </span>
                        <span className="font-medium">{step.title}</span>
                      </button>
                      {index < steps.length - 1 && (
                        <div className={`w-4 h-0.5 rounded ${currentStep > step.number ? 'bg-green-400' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Desktop: Search and Filter Bar (Step 1 only) */}
          {currentStep === 1 && (
            <div className="hidden lg:flex items-center gap-3 bg-white rounded-lg p-2 shadow-sm">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Buscar modelo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#195C75] text-sm"
                />
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>

              <div className="relative" ref={categoryDropdownDesktopRef}>
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg text-sm min-w-[180px] transition-all ${
                    selectedCategory !== 'all' 
                      ? 'border-[#195C75] bg-[#195C75]/5 text-[#195C75]' 
                      : 'border-slate-200 bg-white hover:border-[#195C75]/50'
                  }`}
                >
                  <span className="text-slate-500 text-xs">Categoria:</span>
                  <span className="font-medium">{selectedCategory === 'all' ? 'Todas' : selectedCategory}</span>
                  <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded-full ml-auto">
                    {selectedCategory === 'all' ? totalModels : displayedModels.length}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCategoryDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto min-w-[220px]">
                    <button
                      onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${selectedCategory === 'all' ? 'bg-[#195C75]/10 text-[#195C75] font-medium' : ''}`}
                    >
                      <span>Todas</span>
                      <span className="text-xs text-slate-500">{totalModels}</span>
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.name}
                        onClick={() => { setSelectedCategory(cat.name); setShowCategoryDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${selectedCategory === cat.name ? 'bg-[#195C75]/10 text-[#195C75] font-medium' : ''}`}
                      >
                        <span className="truncate">{cat.name}</span>
                        <span className="text-xs text-slate-500">{cat.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedModelsCount > 0 && (
                <div className="flex items-center bg-[#195C75]/10 text-[#195C75] px-3 py-1.5 rounded-lg text-sm font-medium">
                  {selectedModelsCount} selecionado{selectedModelsCount > 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {/* Mobile: Compact Price + Filter only */}
          <div className="lg:hidden bg-white rounded-lg p-2 shadow-sm mb-2">
            {/* Row 1: Price + Min badge */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-medium text-[#195C75]">Mín: {minQty}</span>
              <div className="flex gap-1">
                {config.prices.map((range, index) => (
                  <div 
                    key={index}
                    className={`px-1.5 py-0.5 rounded border text-center ${
                      totalSelected >= range.min_quantity && (range.max_quantity === null || totalSelected <= range.max_quantity)
                        ? 'border-[#195C75] bg-[#195C75]/10'
                        : 'border-slate-200'
                    }`}
                  >
                    <span className="text-[10px] text-slate-500">{range.max_quantity ? `${range.min_quantity}-${range.max_quantity}` : `${range.min_quantity}+`}</span>
                    <span className="text-xs font-bold text-[#195C75] ml-0.5">R${range.price.toFixed(2).replace('.', ',')}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Row 2: Search + Category in one line */}
            {currentStep === 1 && (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-7 pr-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#195C75] text-sm"
                  />
                  <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                </div>

                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    className={`flex items-center gap-1 px-2 py-1.5 border rounded-lg text-sm whitespace-nowrap ${
                      selectedCategory !== 'all' 
                        ? 'border-[#195C75] bg-[#195C75]/10 text-[#195C75]' 
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <span className="text-xs max-w-[70px] truncate">
                      {selectedCategory === 'all' ? 'Todas' : selectedCategory}
                    </span>
                    <span className="text-[10px] text-slate-500">({selectedCategory === 'all' ? totalModels : displayedModels.length})</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showCategoryDropdown && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[250px] overflow-y-auto min-w-[160px]">
                      <button
                        onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${selectedCategory === 'all' ? 'bg-[#195C75]/10 text-[#195C75] font-medium' : ''}`}
                      >
                        <span>Todas</span>
                        <span className="text-xs text-slate-500">{totalModels}</span>
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat.name}
                          onClick={() => { setSelectedCategory(cat.name); setShowCategoryDropdown(false); }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${selectedCategory === cat.name ? 'bg-[#195C75]/10 text-[#195C75] font-medium' : ''}`}
                        >
                          <span className="truncate">{cat.name}</span>
                          <span className="text-xs text-slate-500">{cat.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-4">

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* STEP 1: Select Models */}
          {currentStep === 1 && (
            <div className="p-6">
              {/* Models Grid */}
              {filteredModels.length === 0 ? (
                <div className="text-center py-12">
                  <Grid3X3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Nenhum modelo encontrado</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {displayedModels.map(model => (
                      <ModelCard
                        key={model.id}
                        model={model}
                        qty={selectedModels[model.id] || 0}
                        onQuantityChange={handleModelQuantityChange}
                        onSetQuantity={handleSetModelQuantity}
                        onPreview={handlePreviewImage}
                      />
                    ))}
                  </div>

                  {/* Infinite scroll loader / Load more button */}
                  {hasMore && (
                    <div ref={loadMoreRef} className="mt-6 py-4 flex flex-col items-center gap-3">
                      {loadingMore ? (
                        <div className="flex items-center gap-2 text-slate-400">
                          <div className="w-5 h-5 border-2 border-slate-300 border-t-[#195C75] rounded-full animate-spin" />
                          <span className="text-sm">Carregando mais...</span>
                        </div>
                      ) : (
                        <button
                          onClick={loadMoreModels}
                          className="px-6 py-2 bg-[#195C75] text-white rounded-lg hover:bg-[#195C75]/90 transition-colors text-sm"
                        >
                          Carregar mais modelos
                        </button>
                      )}
                      <span className="text-xs text-slate-400">
                        Mostrando {displayedModels.length} de {totalModels}
                      </span>
                    </div>
                  )}
                  
                  {/* Show total count */}
                  {!hasMore && displayedModels.length > 0 && (
                    <div className="mt-6 py-4 text-center text-slate-400 text-sm">
                      Mostrando {displayedModels.length} de {totalModels} modelos
                    </div>
                  )}
                  
                  {/* Spacer for fixed bottom bar */}
                  <div className="pb-16"></div>
                </>
              )}

              {/* Next Step Button - removed from here, now fixed at bottom */}
            </div>
          )}

          {/* STEP 2: Summary */}
          {currentStep === 2 && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-800">Etapa 2: Resumo do Pedido</h2>
                <p className="text-sm text-slate-500">Confira os itens e finalize seu pedido</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Items List */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Itens do Pedido
                  </h3>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {/* Selected Models */}
                    {Object.entries(selectedModels).map(([modelId, quantity]) => {
                      if (quantity === 0) return null;
                      const model = models.find(m => m.id === modelId);
                      return (
                        <div key={modelId} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          {model?.image_url && (
                            <img src={resolveImageUrl(model.image_url)} alt={model.name} className="w-12 h-12 rounded-lg object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 text-sm truncate">{model?.name}</p>
                            <p className="text-xs text-slate-500">Modelo pronto</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#195C75]">{quantity} un</p>
                            <p className="text-xs text-slate-500">R$ {(quantity * unitPrice).toFixed(2).replace('.', ',')}</p>
                          </div>
                        </div>
                      );
                    })}

                  </div>

                  {/* Edit buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 text-sm text-[#195C75] hover:bg-[#195C75]/5 py-2 px-3 rounded-lg transition-colors"
                      data-testid="summary-edit-models-btn"
                    >
                      Editar modelos
                    </button>
                  </div>
                </div>

                {/* Summary & Actions */}
                <div className="space-y-4">
                  {/* Observation */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Observação Geral (opcional)
                    </label>
                    <textarea
                      value={orderObservation}
                      onChange={(e) => setOrderObservation(e.target.value)}
                      placeholder="Ex: Entregar até dia 15/01, cores vibrantes, etc..."
                      className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#195C75] text-sm"
                    />
                  </div>

                  {/* Totals */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Quantidade Total</span>
                      <span className="font-semibold">{totalSelected} unidades</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Preço Unitário</span>
                      <span className="font-semibold">R$ {unitPrice.toFixed(2).replace('.', ',')}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold border-t pt-3">
                      <span className="text-slate-800">Total Estimado</span>
                      <span className="text-[#195C75]">R$ {totalPrice.toFixed(2).replace('.', ',')}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={handleAddToCart}
                      className="w-full bg-[#195C75] hover:bg-[#195C75]/90 py-6 text-base"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Adicionar ao Carrinho
                    </Button>
                  </div>
                </div>
              </div>

              {/* Back Button */}
              <div className="mt-6 pt-6 border-t">
                <Button
                  onClick={() => setCurrentStep(1)}
                  variant="outline"
                  data-testid="summary-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar para Seleção de Modelos
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Cart Drawer */}
      <CartDrawer settings={settings} onOpenCustomerModal={() => setShowCustomerModal(true)} />
      
      {/* Fixed Bottom Bar for Step Navigation */}
      {currentStep === 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#195C75] to-[#1a6b87] shadow-lg z-50">
          <div className="max-w-6xl mx-auto px-3 py-2.5 lg:px-4 lg:py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {totalSelected > 0 ? (
                <>
                  <div className="flex items-center gap-1.5 bg-white/20 px-2.5 py-1 rounded-full">
                    <Package className="w-4 h-4 text-white" />
                    <span className="text-white font-semibold text-sm">
                      {totalSelected}
                    </span>
                  </div>
                  <div className="text-white font-bold text-lg">
                    R$ {totalPrice.toFixed(2).replace('.', ',')}
                  </div>
                  {totalSelected < minQty && (
                    <span className="text-white/80 text-xs">Mín. {minQty} un</span>
                  )}
                </>
              ) : (
                <span className="text-white/80 text-sm">Selecione modelos</span>
              )}
            </div>
            <button
              onClick={() => {
                setCurrentStep(2);
                // Track Lead when user advances to summary
                trackLead('Avançar - Seleção de Modelos', 'step1_complete');
              }}
              disabled={!canGoToStep2}
              data-testid="step1-next-btn"
              className="flex items-center gap-1.5 bg-white text-[#195C75] font-semibold px-4 py-2 lg:px-6 lg:py-2.5 rounded-full text-sm lg:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/90 transition-colors"
            >
              Ver Resumo
              <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Customer Modal */}
      <CustomerModal 
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
      />

      {/* Image Preview Lightbox */}
      {imagePreview && (
        <div 
          className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4"
          onClick={() => setImagePreview(null)}
        >
          <button
            onClick={() => setImagePreview(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="max-w-4xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img 
              src={imagePreview.url} 
              alt={imagePreview.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
              <p className="text-white text-center font-medium text-lg">{imagePreview.name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CevadorPage;
